import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiSuccessfulResponse } from '../models/apiSuccessfulResponse';
import { map } from 'rxjs/operators';
import { isInteger, isSafeNumber, parse } from 'lossless-json';

@Injectable({
  providedIn: 'root',
})
export abstract class RestClient {
  constructor(
    protected httpClient: HttpClient,
    @Inject('DS_API') protected rootURL: string,
  ) {}


  getBaseUrl(endpoint: string): string {
    return `${this.rootURL}/${endpoint}`;
  }

  // Auth used to ride along on the cookie via withCredentials; it's now attached explicitly, per
  // request, as a Bearer header by JwtInterceptor.
  defaultOptions: Object = { responseType: 'text' }

  getAsModel<T>(endpoint: string): Observable<T> {
    const url = this.getBaseUrl(endpoint);
    return this.httpClient
      .get(url, this.defaultOptions)
      .pipe(map(this.handleJsonResponse<T>));
  }

  get<T>(endpoint: string, context?: HttpContext): Observable<ApiSuccessfulResponse<T>> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .get(url, context ? { ...this.defaultOptions, context } : this.defaultOptions)
      .pipe(map(this.handleJsonResponse<ApiSuccessfulResponse<T>>));
  }

  post(endpoint: string, data: any, context?: HttpContext): Observable<any> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .post(url, data, context ? { ...this.defaultOptions, context } : this.defaultOptions)
      .pipe(map(this.handleJsonResponse<any>));
  }

  postDirect<T>(endpoint: string, data: any): Observable<T> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .post(url, data, this.defaultOptions)
      .pipe(map(this.handleJsonResponse<T>));
  }

  deleteForResponse<T>(url: string, data: any = null): Observable<T> {
    return this.httpClient
      .delete(this.getBaseUrl(url), { params: data, responseType: 'text' })
      .pipe(map(this.handleJsonResponse<T>));
  }

  postForResponse<T>(endpoint: string, data: any, context?: HttpContext): Observable<T> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .post(url, data, context ? { ...this.defaultOptions, context } : this.defaultOptions)
      .pipe(map(this.handleJsonResponse<T>));
  }


  patch<T>(endpoint: string, data: any = null): Observable<T> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .patch(url, data, this.defaultOptions)
      .pipe(map(this.handleJsonResponse<T>));
  }

  put<T>(endpoint: string, data: any = null): Observable<T> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .put(url, data, this.defaultOptions)
      .pipe(map(this.handleJsonResponse<T>));
  }

  delete<T>(endpoint: string, data: any = null): Observable<T | HttpEvent<T>> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .delete(url, { params: data, responseType: 'text' })
      .pipe(map(this.handleJsonResponse<T>));
  }

  // Unlike get<T>, returns the response body as the server wrote it rather than wrapping it in
  // ApiSuccessfulResponse. Takes a context for the same reason get does - it is how a call opts
  // into interceptor behaviour, and the heartbeat's EXPIRE_SESSION_ON_401 depends on it.
  getDirect<T>(endpoint: string, context?: HttpContext): Observable<T> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient
      .get(url, context ? { ...this.defaultOptions, context } : this.defaultOptions)
      .pipe(map(this.handleJsonResponse<T>));
  }

  getFile(endpoint: string) {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient.get<any>(url, {
      responseType: 'arraybuffer' as 'json',
    });
  }

  postFile(endpoint: string, data: any): Observable<any> {
    let url = this.getBaseUrl(endpoint);
    return this.httpClient.post(this.getBaseUrl(endpoint), data, {
      responseType: 'arraybuffer',
    });
  }

  openFile(endpoint: string, contentType: string) {
    this.getFile(endpoint).subscribe((data) => {
      const file = new Blob([data], { type: contentType });
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl);
    });
  }

  public handleJsonResponse<T>(response: string): T {
    try {
      if (response === null || response === undefined || response === '') {
        return null;
      }

      //ensure the response is a string
      if (typeof response !== 'string') {
        return response as T;
      }

      return parse(response, null, customNumberParser) as T;
    } catch (error) {
      console.error(error);
      return response as T;
    }
  }

}

@Injectable({
  providedIn: 'root',
})
export abstract class ODataClient extends RestClient {
  private baseQueryEndpoint: string;
  private odataQuery: string;

  constructor(
    protected httpClient: HttpClient,
    @Inject('DS_API') protected rootURL: string,
  ) {
    super(httpClient, rootURL);
  }

  search(baseQueryEndpoint: string): ODataClient {
    this.baseQueryEndpoint = baseQueryEndpoint;
    this.odataQuery = null;
    return this;
  }

  private setOdataOperation(predicate: string) {
    if (this.odataQuery) {
      this.odataQuery += `&${predicate}`;
    } else {
      this.odataQuery = `?${predicate}`;
    }
  }
  filter(filterQuery: string): ODataClient {
    this.setOdataOperation(`$filter=${filterQuery}`);
    return this;
  }

  orderBy(orderBy: string): ODataClient {
    this.setOdataOperation(`$orderby=${orderBy}`);
    return this;
  }

  includeCount(): ODataClient {
    this.setOdataOperation('$count=true');
    return this;
  }

  page(pageNum: number, pageSize: number) {
    this.setOdataOperation(
      `$skip=${(pageNum - 1) * pageSize}&$top=${pageSize}`,
    );
    this.includeCount();
    return this;
  }

  expand(propertyNames: string) {
    this.setOdataOperation(`$expand=${propertyNames}`);
    return this;
  }

  /*
   * OData Query Support
   https://docs.microsoft.com/en-us/aspnet/web-api/overview/odata-support-in-aspnet-web-api/supporting-odata-query-options
   */
  query<T>(): Observable<T[]> {
    let endpoint: string = this.getBaseUrl(this.baseQueryEndpoint);
    if (this.odataQuery) {
      endpoint += this.odataQuery;
    }

    return this.httpClient.get<T[]>(endpoint);
  }
}

@Injectable({
  providedIn: 'root',
})
export class DSRESTClient extends RestClient {
  constructor(
    protected httpClient: HttpClient,
    @Inject('DS_API') private DS_API: string,
  ) {
    super(httpClient, DS_API);
  }
}

export function customNumberParser(value) {
  return isInteger(value)
    ? isSafeNumber(value)
      ? parseInt(value)
      : value
    : parseFloat(value);
}
